import { Component, h, State, Prop, Watch, Event, Listen, EventEmitter, forceUpdate, Element } from '@stencil/core';
import { ActionTrackingEvent } from '@union-investment/tracking/src/events';

interface UiSuggestion {
  text: string;
  url: string;
  id: number;
}

interface UiSuggestionGroup {
  headline: string;
  hits: UiSuggestion[];
}

@Component({
  tag: 'ui-cms-search-autosuggest',
  styleUrl: 'ui-cms-search-autosuggest.scss',
})
export class UiCmsSearchAutosuggest {
  @Element() el: HTMLElement;

  @Event() suggest: EventEmitter<{ query: string }>;

  @Event() clear: EventEmitter;

  @Event() search: EventEmitter;

  @Prop() minLength = 0;

  @Prop() suggestionUrl = '';

  @Prop() apiKey = '';

  @Prop() searchUrl = '?q=%VALUE%';

  @State() suggestionGroups: UiSuggestionGroup[] = [];

  @State() focusedSuggestion: number = null;

  @State() searchValue = '';

  @Prop() inputInFocus: boolean;

  // listening for a click on search icon in input filed
  @Listen('inputIconClicked')
  reactOnSearchIconClicked() {
    window.ui.services?.tracking?.track({
      attributes: {
        elementType: 'Button',
        elementLabel: 'Suchen',
        target: 'Startseite.Suche.Suchergebnisseite',
      },
      eventInfo: {
        eventName: 'Manual search',
        eventType: 'Action',
      },
      category: {
        area: 'Content',
      },
    } as ActionTrackingEvent);
    this.onSubmit(this.searchValue);
  }

  componentDidLoad() {
    window.ui?.services?.i18n?.registerI18nUpdateCallback(() => {
      forceUpdate(this);
    });
  }

  componentWillLoad() {
    if (this.searchUrl[0] === '#') {
      this.searchUrl = `${window.location.pathname}?q=%VALUE%`;
    }
  }

  @Watch('searchValue')
  async triggerSearch() {
    if (this.searchValue.length > this.minLength && this.suggestionUrl) {
      this.suggest.emit();
      const req = await fetch(
        `${this.suggestionUrl}&keyword=${this.searchValue}`,
        // eslint-disable-next-line prefer-regex-literals
        {
          headers: {
            'x-api-key': this.apiKey,
          },
        }
      );
      let i = 0;
      if (req.ok) {
        const suggestionGroups: UiSuggestionGroup[] = (await req.json()).results;
        this.suggestionGroups = suggestionGroups.map((suggestionGroup) => ({
          ...suggestionGroup,
          hits: suggestionGroup.hits.map((suggestion) => {
            i += 1;
            return {
              ...suggestion,
              id: i - 1,
            };
          }),
        }));
        this.focusedSuggestion = null;
        return;
      }
    }
    this.suggestionGroups = [];
    this.clear.emit();
  }

  keyCodes = {
    enter: 'Enter',
    up: 'ArrowUp',
    upIe11: 'Up',
    down: 'ArrowDown',
    downIe11: 'Down',
    esc: 'Escape',
  };

  get allSuggestions(): UiSuggestion[] {
    return this.suggestionGroups.reduce((aggr: UiSuggestion[], group) => [...aggr, ...group.hits], []);
  }

  get activeSuggestion(): UiSuggestion {
    return this.allSuggestions.find((suggestion) => suggestion.id === this.focusedSuggestion);
  }

  onChange(event: CustomEvent & KeyboardEvent) {
    this.searchValue = event.detail.value;
  }

  clearInput() {
    this.searchValue = '';
  }

  openPage(url: string) {
    window.open(url, '_self');
  }

  fireSearch(query: string) {
    const searchQuery = new URLSearchParams(query);
    this.search.emit({ query: searchQuery.get('q') || query });
    // eslint-disable-next-line prefer-regex-literals
    this.openPage(this.searchUrl.replace('q=%VALUE%', query));
  }

  onSubmit(query: string) {
    const searchQuery = new URLSearchParams(query);
    if (searchQuery.get('q')) {
      this.fireSearch(searchQuery.toString());
    } else {
      const newSearchQuery = new URLSearchParams();
      newSearchQuery.set('q', query);
      this.fireSearch(newSearchQuery.toString());
    }
  }

  onAutoSuggestSubmit(event: CustomEvent) {
    this.onSubmit(event.detail.value);
  }

  onSuggestionClick(suggestion: UiSuggestion) {
    window.ui.services?.tracking?.track({
      attributes: {
        elementType: 'Link',
        elementLabel: suggestion.text,
        target: 'Startseite.Suche.Suchergebnisseite',
      },
      eventInfo: {
        eventName: 'Search suggestion clicked',
        eventType: 'Action',
      },
      category: {
        area: 'Content',
      },
    } as ActionTrackingEvent);
    // eslint-disable-next-line compat/compat
    this.onSubmit(suggestion.text);
  }

  render() {
    return (
      <ui-autosuggest
        onUiAutosuggestChange={this.onChange.bind(this)}
        onUiAutosuggestSubmit={this.onAutoSuggestSubmit.bind(this)}
        value={this.searchValue}
        placeholder={window.ui?.services?.i18n?.translate('search')}
        icon="magnifying-glass"
        inputInFocus={this.inputInFocus}
      >
        {this.suggestionGroups.map((suggestionGroup, i) => (
          <ui-suggestion-group key={suggestionGroup.headline + i} title={suggestionGroup.headline}>
            {suggestionGroup.hits?.map((suggestion) => (
              <ui-suggestion
                class={{ focus: suggestion.id === this.focusedSuggestion }}
                key={suggestion.id}
                value={suggestion.text}
                onClick={() => this.onSuggestionClick(suggestion)}
                innerHTML={suggestion.text.replace(
                  new RegExp(`(${this.searchValue})`, 'gi'),
                  '<span class="highlight">$1</span>'
                )}
                tabindex="0"
              ></ui-suggestion>
            ))}
          </ui-suggestion-group>
        ))}
      </ui-autosuggest>
    );
  }
}
